import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronDown, MapPin, Globe, Eye, Target, Flag } from 'lucide-react';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { ABOUT_HERO, values, milestones, divisions, AWARD_IMAGES, VISION, MISSION, GROUP_STATS } from '@/data/about';

const h2 = "font-heading font-black text-3xl sm:text-4xl text-[#121212] tracking-tight";
const h2Lg = "font-heading font-black text-3xl sm:text-4xl lg:text-5xl text-[#121212] tracking-tight";

export function AboutHero() {
  return (
    <section data-testid="about-hero" className="relative min-h-[68vh] flex items-center overflow-hidden bg-[#121212]">
      <img src={ABOUT_HERO} alt="Eurogulf Mobility Group across the UAE" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/25" />
      <div className="relative z-10 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 pt-24">
        <span className="chip-orange">About the Group</span>
        <h1 className="mt-5 font-heading font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.03] tracking-tight text-white max-w-3xl">A legacy of movement, since 1976.</h1>
        <p className="mt-6 font-body text-base sm:text-lg text-white/80 max-w-2xl leading-relaxed">
          Eurogulf Mobility Group was established in 1976 with the Europcar franchise for Dubai and the Northern Emirates. Today it is one of the region's largest and most diversified transportation providers, spanning self-drive rental, leasing, chauffeur, bus &amp; coach, commercial vehicles and used-car remarketing, under one platform.
        </p>
      </div>
    </section>
  );
}

export function WhoWeAre() {
  return (
    <>
      <section className="bg-white py-16 sm:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="chip-orange">Who We Are</span>
          <h2 className={`mt-4 ${h2}`}>One connected mobility ecosystem.</h2>
          <div className="mt-6 space-y-4 font-body text-base text-[#555555] leading-relaxed">
            <p>Eurogulf Mobility Group is a diversified mobility and transportation platform serving customers across every vehicle and service category. The group combines self-drive rental, leasing, chauffeur-driven transport, bus &amp; coach operations, commercial vehicle support and used-vehicle sales into one connected ecosystem.</p>
            <p>Backed by a dedicated team of over 1,200 professionals and a fleet of more than 10,000 vehicles across 14 locations throughout the UAE, each brand in the portfolio is designed to serve a specific mobility need, while benefiting from the strength of the wider group.</p>
          </div>
        </div>
      </section>

      <section data-testid="about-group-stats" className="bg-white border-y border-black/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4 divide-x divide-black/5">
          {GROUP_STATS.map((s) => (
            <div key={s.label} className="py-8 px-3 text-center">
              <p className="font-heading font-black text-3xl lg:text-4xl text-[#EE5A01] tracking-tight">{s.value}</p>
              <p className="mt-1 font-body text-xs lg:text-sm text-[#666666]">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#FAFAFA] py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-5 gap-6 items-stretch">
          <div className="lg:col-span-3 card-light p-8">
            <div className="flex items-center gap-3 mb-3">
              <span className="grid h-10 w-10 place-items-center rounded-lg bg-[#EE5A01] text-white"><MapPin className="w-5 h-5" /></span>
              <h3 className="font-heading font-black text-xl sm:text-2xl text-[#121212] tracking-tight">UAE Presence</h3>
            </div>
            <p className="font-body text-base text-[#555555] leading-relaxed">
              Headquartered in Al Quoz 3, Dubai, the group is supported by a broader network of operating locations across the UAE through the businesses within its portfolio. This structure helps maintain stronger service access, more responsive support and better operational continuity across different customer needs.
            </p>
          </div>
          <div className="lg:col-span-2 card-light p-8">
            <div className="flex items-center gap-3 mb-3">
              <span className="grid h-10 w-10 place-items-center rounded-lg bg-[#121212] text-white"><Globe className="w-5 h-5" /></span>
              <h3 className="font-heading font-black text-xl sm:text-2xl text-[#121212] tracking-tight">How the Portfolio Works</h3>
            </div>
            <p className="font-body text-base text-[#555555] leading-relaxed">Each brand is built for a specific mobility use case, from a single airport rental to a 200-coach staff operation, while drawing on shared workshops, a 24/7 control room and 50 years of fleet expertise.</p>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
          <div className="rounded-2xl border border-[#EE5A01]/20 bg-[#FFF4ED] p-6">
            <p className="font-body text-sm text-[#555555] leading-relaxed">
              Europcar is a central part of that ecosystem in Dubai and the Northern Emirates, with service coverage across Dubai, Sharjah, Ajman, Ras Al Khaimah, Fujairah and Umm Al Quwain, while Abu Dhabi city operations remain under a separate franchise structure.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}

function TimelineItem({ milestone, index, isActive, onClick }) {
  const isEven = index % 2 === 0;
  return (
    <div className={`relative flex items-start gap-6 mb-10 ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'}`} data-testid={`timeline-item-${index}`}>
      <div className={`flex-1 ${isEven ? 'lg:text-right' : 'lg:text-left'}`}>
        <button onClick={onClick} className="text-left lg:text-inherit w-full">
          <span className="font-mono text-xs tracking-[0.2em] text-[#EE5A01] uppercase">{milestone.year}</span>
          <h3 className="font-heading font-bold text-lg text-[#121212] mt-1 mb-2">{milestone.title}</h3>
          <div className={`overflow-hidden transition-all duration-500 ${isActive ? 'max-h-48 opacity-100' : 'max-h-0 opacity-0'}`}>
            <p className="font-body text-sm text-[#666666] leading-relaxed">{milestone.desc}</p>
          </div>
          {!isActive && <ChevronDown className="w-4 h-4 text-[#EE5A01] mt-1 inline-block" />}
        </button>
      </div>
      <div className="flex flex-col items-center flex-shrink-0 pt-1">
        <div className={`timeline-node ${isActive ? 'active' : ''}`} onClick={onClick} />
      </div>
      <div className="flex-1 hidden lg:block" />
    </div>
  );
}

export function Timeline() {
  const [active, setActive] = useState(0);
  const [ref, visible] = useScrollAnimation();
  return (
    <section data-testid="timeline-section" className="bg-white py-16 sm:py-24 border-t border-black/5">
      <div ref={ref} className="max-w-4xl mx-auto px-4">
        <div className={`text-center mb-14 ${visible ? 'scroll-visible' : 'scroll-hidden'}`}>
          <div className="orange-accent-line mx-auto mb-6" />
          <h2 className={h2Lg}>Our Journey</h2>
          <p className="mt-4 font-body text-base text-[#666666] max-w-xl mx-auto">Nearly five decades of building the UAE's most complete mobility platform.</p>
        </div>
        <div className="relative">
          <div className="absolute left-[calc(50%-1px)] top-0 bottom-0 w-[2px] bg-[#EE5A01]/20 hidden lg:block" />
          <div className="absolute left-[7px] top-0 bottom-0 w-[2px] bg-[#EE5A01]/20 lg:hidden" />
          {milestones.map((m, i) => (
            <TimelineItem key={m.year} milestone={m} index={i} isActive={active === i} onClick={() => setActive(active === i ? -1 : i)} />
          ))}
        </div>
      </div>
    </section>
  );
}

export function VisionValues() {
  return (
    <section data-testid="about-vision" className="bg-[#FAFAFA] py-16 sm:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-14">
          <div className="card-light p-8">
            <Eye className="w-8 h-8 text-[#EE5A01] mb-4" strokeWidth={1.5} />
            <h2 className="font-heading font-black text-2xl text-[#121212] tracking-tight mb-3">Our Vision</h2>
            <p className="font-body text-base text-[#555555] leading-relaxed">{VISION}</p>
          </div>
          <div className="card-light p-8">
            <Target className="w-8 h-8 text-[#EE5A01] mb-4" strokeWidth={1.5} />
            <h2 className="font-heading font-black text-2xl text-[#121212] tracking-tight mb-3">Our Mission</h2>
            <p className="font-body text-base text-[#555555] leading-relaxed">{MISSION}</p>
          </div>
        </div>
        <div className="text-center mb-10">
          <Flag className="w-6 h-6 text-[#EE5A01] mx-auto mb-3" />
          <h2 className="font-heading font-black text-2xl sm:text-3xl text-[#121212] tracking-tight">Our F.A.I.R. Values</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {values.map((v) => (
            <div key={v.letter} data-testid={`about-value-${v.letter}`} className="card-light p-6">
              <span className="font-heading font-black text-4xl text-[#EE5A01]">{v.letter}</span>
              <h3 className="font-heading font-bold text-base text-[#121212] mt-2 mb-2">{v.title}</h3>
              <p className="font-body text-sm text-[#666666] leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function BrandsGrid() {
  const [ref, visible] = useScrollAnimation();
  const reveal = visible ? 'scroll-visible' : 'scroll-hidden';
  return (
    <section data-testid="about-divisions" className="bg-white py-16 sm:py-24 border-t border-black/5">
      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center mb-14 ${reveal}`}>
          <div className="orange-accent-line mx-auto mb-6" />
          <h2 className={h2Lg}>Our Divisions</h2>
          <p className="mt-4 font-body text-base text-[#666666] max-w-xl mx-auto">Seven trusted brands, working together to move people and businesses across the UAE.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {divisions.map((d, i) => (
            <Link key={`${d.name}-${i}`} to={d.href} data-testid={`about-division-${i}`} className={`card-light p-6 group flex flex-col ${reveal} stagger-${(i % 4) + 1}`}>
              <div className="w-full h-9 flex items-center mb-5">
                <img src={d.logo} alt={d.name} className="h-8 w-auto max-w-[70%] object-contain object-left opacity-90 group-hover:opacity-100 transition-opacity" loading="lazy" />
              </div>
              <h3 className="font-heading font-bold text-base text-[#121212] mb-2 group-hover:text-[#EE5A01] transition-colors">{d.name}</h3>
              <p className="font-body text-sm text-[#666666] leading-relaxed mb-4 flex-1">{d.desc}</p>
              <span className="inline-flex items-center gap-1 text-[#EE5A01] text-xs font-heading font-bold group-hover:gap-2 transition-all">Learn More <ArrowRight className="w-3.5 h-3.5" /></span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export function AwardsGrid() {
  return (
    <section data-testid="awards-section" className="bg-[#FAFAFA] py-16 sm:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <div className="orange-accent-line mx-auto mb-6" />
          <h2 className={`${h2Lg} mb-4`}>Awards &amp; Recognition</h2>
          <p className="font-body text-base text-[#666666] max-w-xl mx-auto">Our commitment to excellence has been honoured with prestigious awards and international certifications.</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
          {AWARD_IMAGES.map((a) => (
            <div key={a.title} className="card-light p-5 text-center group">
              <div className="w-full h-20 flex items-center justify-center mb-4">
                <img src={a.img} alt={a.title} className="max-h-20 w-auto object-contain group-hover:scale-105 transition-transform" loading="lazy" />
              </div>
              <h3 className="font-heading font-bold text-xs text-[#121212] mb-1 uppercase tracking-wider">{a.title}</h3>
              <p className="font-body text-[10px] text-[#666666] leading-relaxed">{a.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
